import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getGates, updateGate, createGate, getGateDuplicateAnalysis, mergeGates, cleanupOrphanGates } from '../api/gates';
import { Gate } from '../types/Gate';

export const useGates = (queryString: string = '') =>
  useQuery({
    queryKey: ['gates', queryString],
    queryFn: () => getGates(queryString),
  });

export const useGatesWMinRoute = () =>
  useQuery({
    queryKey: ['gates', 'minimal_route'],
    queryFn: () => getGates('scope=minimal_route'),
  });

export const useUpdateGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gateId, data }: { gateId: string; data: Partial<Gate> }) =>
      updateGate(gateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gates'] });
      queryClient.invalidateQueries({ queryKey: ['runAndCalibration'] });
      queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });
};

export const useCreateGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Gate>) => createGate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gates'] });
    },
  });
};

export const useGateDuplicateAnalysis = (enabled: boolean = false) =>
  useQuery({
    queryKey: ['gates', 'duplicate_analysis'],
    queryFn: getGateDuplicateAnalysis,
    enabled,
  });

export const useMergeGates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (gateIds: string[]) => mergeGates(gateIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gates'] });
      queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });
};

export const useCleanupOrphanGates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => cleanupOrphanGates(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gates'] });
    },
  });
};
